<template>
  <div class="ticket-packages-root">
    <section
        class="section-tickets"
        :class="[
            {
                'is-filter-drawer-open': isFilterDrawerOpen,
                'section-tickets--offer-dock':
                    showOfferAnchor && offerAnchorPlacement === 'dock'
            },
            htmlClass
        ]"
        :id="htmlId"
        v-if="items.length > 0"
    >
        <div class="container container-title" v-if="title || description">
            <h2 class="ticket-packages__title" v-if="title">
                {{ title }}
            </h2>

            <div class="ticket-packages__description" v-if="description">
                <span v-html="description"></span>
            </div>
        </div>

        <ticket-packages-filter
            v-if="!showItemsOnly"
            :config="config"
            :filters="filters"
            :filter-options="filterOptions"
            :total-tickets="filteredItems.length"
            :trans="{
                filters: trans.tickets.filters,
                promocode: trans.tickets.promocode,
                domain: trans.domain
            }"
            @filter-change="handleFilterChange"
            @promocode-change="handlePromocodeChange"
            @filter-open="handleFilterOpen"
        />

        <div class="container">
            <div class="ticket-list">
                <ticket-packages-header
                    v-if="!showItemsOnly"
                    :total-tickets="filteredItems.length"
                    @sort-change="handleSortChange"
                    :trans="trans.tickets"
                />

                <ticket-packages-list
                    :page="page"
                    :items="paginatedItems"
                    :promotional-info="promotionalInfo"
                    :priceType="priceType"
                    :lang="lang"
                    :config="config"
                    :html-id="htmlId"
                    :trans="{
                        tickets: trans.tickets,
                        seat: trans.seat
                    }"
                    @map-preview="handleMapPreview"
                />

                <div
                    v-if="!hasReachedEnd"
                    ref="loadMoreSentinel"
                    class="load-more-container"
                    aria-live="polite"
                >
                    <span
                        v-if="isLoadingMore"
                        class="load-more-status"
                    >
                        {{ trans.tickets.loading }}
                    </span>
                    <button
                        v-else
                        type="button"
                        class="button button-primary button-outline load-more-button"
                        @click="loadMoreItems"
                    >
                        {{ trans.tickets.load_more }}
                    </button>
                </div>

                <div
                    v-if="hasReachedEnd && !showItemsOnly && currentPage > 1"
                    class="end-of-results"
                >
                    <p>{{ trans.tickets.you_have_seen_all_tickets }}</p>
                </div>
            </div>
            <div
                class="ticket-map"
                v-if="!showItemsOnly"
                ref="ticketMapColumn"
            >
                <div class="ticket-map-sticky" ref="ticketMapSticky">
                    <ticket-packages-filter-map
                        :filters="filters"
                        :filter-options="filterOptions"
                        @filter-change="handleFilterChange"
                        :availables="availables"
                        :highlighted-items="highlightedItems"
                        :hover-preview="mapHoverPreview"
                        :trans="trans.tickets"
                    />
                    <ticket-packages-offer-anchor
                        v-if="
                            primaryOffer &&
                                showOfferAnchor &&
                                offerAnchorPlacement === 'map'
                        "
                        placement="map"
                        :visible="true"
                        :offer-label="offerAnchorLabel"
                        :urgency-label="offerAnchorUrgencyLabel"
                        :deal-icon-code="offerAnchorDealIconCode"
                        :title="primaryOffer.location || primaryOffer.title"
                        :sector-label="offerAnchorSectorLabel"
                        :location-type="primaryOffer.type || null"
                        :ticket-type="primaryOffer.ticket_type || null"
                        :ticket-type-trans="trans.seat || {}"
                        :date="primaryOffer.date || null"
                        :day-of-week="primaryOffer.dayOfWeek || null"
                        :group="primaryOffer.group || null"
                        :includes="primaryOffer.includes || []"
                        :includes-label="
                            (trans.tickets && trans.tickets.package_includes) ||
                                'Includes package'
                        "
                        :offer-title="offerAnchorCopy.title"
                        :offer-description="offerAnchorCopy.description"
                        :price="primaryOffer.price"
                        :price-old="primaryOffer.price_old"
                        :discount-percent="
                            primaryOffer.offer_discount_percent
                        "
                        :price-trans="trans.tickets || {}"
                        :cta-label="offerAnchorCtaLabel"
                        @cta="handleOfferAnchorCta"
                    />
                </div>
            </div>
        </div>

        <ticket-packages-offer-anchor
            v-if="
                primaryOffer &&
                    !showItemsOnly &&
                    showOfferAnchor &&
                    offerAnchorPlacement === 'dock'
            "
            placement="dock"
            :visible="true"
            :offer-label="offerAnchorLabel"
            :urgency-label="offerAnchorUrgencyLabel"
            :deal-icon-code="offerAnchorDealIconCode"
            :title="primaryOffer.location || primaryOffer.title"
            :sector-label="offerAnchorSectorLabel"
            :location-type="primaryOffer.type || null"
            :ticket-type="primaryOffer.ticket_type || null"
            :ticket-type-trans="trans.seat || {}"
            :date="primaryOffer.date || null"
            :day-of-week="primaryOffer.dayOfWeek || null"
            :group="primaryOffer.group || null"
            :includes="primaryOffer.includes || []"
            :includes-label="
                (trans.tickets && trans.tickets.package_includes) ||
                    'Includes package'
            "
            :offer-title="offerAnchorCopy.title"
            :offer-description="offerAnchorCopy.description"
            :price="primaryOffer.price"
            :price-old="primaryOffer.price_old"
            :discount-percent="primaryOffer.offer_discount_percent"
            :price-trans="trans.tickets || {}"
            :cta-label="offerAnchorCtaLabel"
            @cta="handleOfferAnchorCta"
        />

    </section>

    <ticket-quiz-launcher
        v-if="enableTicketQuiz"
        :trans="trans.ticketQuiz"
        @shown="handleQuizLauncherShown"
        @open="handleQuizOpen"
        @minimized="handleQuizMinimized"
    />
    <ticket-quiz-modal
        v-if="enableTicketQuiz"
        :open="quizOpen"
        :ols-base="ticketQuizConfig.olsShopApiUrl || ''"
        :lang="lang"
        :trans="trans.ticketQuiz"
        :tickets="tickets"
        :promo-codes="ticketQuizConfig.promoCodes || {}"
        :promo-soft-fail="!!ticketQuizConfig.promoSoftFail"
        :promo-site="(trans.domain && trans.domain.site) || ''"
        :product-slugs="ticketQuizConfig.productSlugs || {}"
        :page="page"
        :currency-symbol="quizCurrencySymbol"
        @close="quizOpen = false"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../sass/variables";

.section-tickets {
    background-color: var(--color-white);

    // Fixed offer bar at bottom — keep list clear of the overlay
    &--offer-dock {
        @media (max-width: 799px) {
            > .container {
                padding-bottom: 110px; // editorial dock card + safe-area clearance
            }
        }
    }
}

.ticket-packages {
    position: relative;
    width: 100%;
    margin: 16px auto 0;
    padding-bottom: 16px;
    background: var(--color-white);

    &.is-filter-drawer-open {
        z-index: 100;
    }
}

.container {
    display: flex;
    gap: 32px;
}

.container.container-title {
    display: block;
    text-align: center;
}

.ticket-list {
    width: 100%;
}

.ticket-map {
    max-width: 300px;
    min-width: 290px;
    width: 100%;

    @media (max-width: 991px) {
        display: none;
    }

    @media (min-width: 1200px) {
        max-width: 340px;
    }
}

.ticket-map-sticky {
    position: sticky;
    top: 80px;
}

.load-more-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 36px;
    margin: 40px 0;

    .load-more-button {
        background-color: $color2-gray-lightest;
        font-weight: 600;
        min-width: 180px;
        cursor: pointer;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .load-more-status {
        color: $color2-text-secondary;
        font-size: 14px;
        font-weight: 600;
    }
}

.end-of-results {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;

    p {
        color: $color2-text-secondary;
        font-size: 16px;
        text-align: center;
        font-style: italic;
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>

<script>
import TicketPackagesFilter from "./TicketPackagesFilter.vue";
import TicketPackagesFilterMap from "./TicketPackagesFilterMap.vue";
import TicketPackagesHeader from "./TicketPackagesHeader.vue";
import TicketPackagesList from "./TicketPackagesList.vue";
import TicketPackagesOfferAnchor from "./TicketPackagesOfferAnchor.vue";
import TicketQuizLauncher from "./TicketQuizLauncher.vue";
import TicketQuizModal from "./TicketQuizModal.vue";
import {
    pushDataLayer,
    createEventId,
    resolveCurrency,
    ticketToItem,
    trackSelectContent,
    trackViewItemList
} from "../../../utils/gtm";
import { currencySymbol, formatMoney } from "../../../utils/money";
import {
    fetchOfferOverlays,
    recordOfferClick,
    resolveAttributionToken,
    resolveDisplayFlag,
    resolveOfferAssignmentId,
    resolveOfferHighlights,
    resolveOfferPricing,
    resolveOfferToken,
    resolveOfferUrgencyLabel,
    saveOfferHandoff
} from "../../../utils/offerOverlay";

const OFFER_ANCHOR_MIN_SPACE_PX = 220;
// Sticky filter band — dock sits flush under this (see OfferAnchor $filter-sticky-height).
const OFFER_FILTER_STICKY_HEIGHT_PX = 65;
// IntersectionObserver thresholds (dock gating uses geometry in syncOfferCardVisibilityFromDom).
const OFFER_CARD_VISIBILITY_RATIO = 0.01;

const DATE_LOCALES = {
    pt: "pt-BR",
    en: "en-US",
    es: "es-ES",
    it: "it-IT",
    fr: "fr-FR",
    tr: "tr-TR",
    de: "de-DE"
};

function resolveDateLocale(lang) {
    return DATE_LOCALES[lang] || "en-US";
}

export default {
    components: {
        TicketPackagesFilter,
        TicketPackagesHeader,
        TicketPackagesFilterMap,
        TicketPackagesList,
        TicketPackagesOfferAnchor,
        TicketQuizLauncher,
        TicketQuizModal
    },
    props: {
        title: {
            type: String,
            required: false
        },
        description: {
            type: String,
            required: false
        },
        htmlId: {
            type: String,
            required: false
        },
        htmlClass: {
            type: String,
            required: false
        },
        page: {
            type: Object,
            required: true
        },
        tickets: {
            type: Array,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        trans: {
            type: Object,
            required: true
        },
        lang: {
            type: String,
            required: true
        },
        priceType: {
            type: String,
            required: false
        },
        showItemsOnly: {
            type: Boolean,
            required: false,
            default: false
        },
        locations: {
            type: Array,
            required: false,
            default: () => []
        },
        enableTicketQuiz: {
            type: Boolean,
            required: false,
            default: false
        },
        ticketQuizConfig: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },
    data() {
        return {
            quizOpen: false,
            locationsWithoutPromoCode: [
                "luxury-suite",
                "folia-tropical",
                "super-folia",
                "camarote-maravilha"
            ],
            filters: {
                dates: [],
                types: [],
                prices: [],
                inclusions: [],
                locationAndSector: []
            },
            availables: [],
            highlightedItems: [],
            mapHoverPreview: null,
            // Configurações de paginação
            itemsPerPage: 20,
            currentPage: 1,
            isLoadingMore: false,
            hasReachedEnd: false,
            promotionalInfo: null,
            sortBy: "most-relevant",
            ticketType: "",
            isFilterDrawerOpen: false,
            offerOverlaysBySku: {},
            loadMoreObserver: null,
            onWindowScrollLoadMore: null,
            isOfferCardVisible: true,
            offerAnchorPlacement: "none",
            offerVisibilityObserver: null,
            onWindowScrollOfferAnchor: null
        };
    },
    computed: {
        quizCurrencySymbol() {
            return currencySymbol(this.config && this.config.currency);
        },
        primaryOffer() {
            const list = this.filteredItems || [];
            for (let i = 0; i < list.length; i++) {
                if (list[i] && list[i].has_offer) {
                    return list[i];
                }
            }
            return null;
        },
        showOfferAnchor() {
            return Boolean(
                this.primaryOffer &&
                    !this.showItemsOnly &&
                    !this.isOfferCardVisible
            );
        },
        // Mobile dock visible → hide header-mobile tab bar (body.offer-dock-active)
        showOfferDockChrome() {
            return Boolean(
                this.showOfferAnchor && this.offerAnchorPlacement === "dock"
            );
        },
        offerAnchorLabel() {
            return (
                (this.trans.tickets && this.trans.tickets.todays_offer) ||
                (this.trans.tickets && this.trans.tickets.special_offer) ||
                "Today's offer"
            );
        },
        offerAnchorCtaLabel() {
            return (
                (this.trans.tickets && this.trans.tickets.see_details) ||
                "See details"
            );
        },
        offerAnchorUrgencyLabel() {
            const offer = this.primaryOffer;
            if (!offer) return null;
            const fromOffer = String(offer.offer_urgency_label || "").trim();
            if (fromOffer) return fromOffer;
            return (
                (this.trans.tickets &&
                    this.trans.tickets.limited_time_offer) ||
                "Limited-time offer"
            );
        },
        offerAnchorDealIconCode() {
            const offer = this.primaryOffer;
            if (!offer || !offer.flag) return null;
            const code = Number(offer.flag.code);
            return Number.isFinite(code) ? code : null;
        },
        offerAnchorSectorLabel() {
            const offer = this.primaryOffer;
            if (!offer || !offer.sector) return null;
            const sectorWord =
                (this.trans.tickets && this.trans.tickets.sector) ||
                (this.trans.seat && this.trans.seat.sector) ||
                "Sector";
            let label = `${sectorWord} ${offer.sector}`;
            if (offer.rows) {
                const rowWord =
                    (this.trans.tickets && this.trans.tickets.row) || "Row";
                label = `${label} | ${rowWord} ${offer.rows}`;
            }
            return label;
        },
        offerAnchorCopy() {
            const offer = this.primaryOffer;
            const copy = (offer && offer.offer_copy) || {};
            return {
                title: String(copy.title || "").trim(),
                description: String(copy.description || "").trim()
            };
        },
        itemsFiltered() {
            // enable / on-request / programmed / available-soon: mostram na lista.
            // soldout listado (código 4 → status disable): mostra, com CTA bloqueado.
            // soldout oculto (código 1): a API não envia; se chegar, também mostra.
            // disable só por estoque/custo (soldout===0): esconde (senão inundam a lista).
            return this.tickets.filter(ticket => {
                if (ticket.status === "enable") return true;
                if (Number(ticket.soldout) === 4 || Number(ticket.soldout) === 1) return true;
                if (ticket.status && ticket.status !== "disable") return true;
                return false;
            });
        },
        items() {
            return this.itemsFiltered.map(ticket => {
                this.availables = [
                    ...new Set(
                        this.availables.concat(
                            `${ticket.location_slug}-${ticket.sector}`
                        )
                    )
                ];

                let formattedDate = ticket.small_date;
                let dayOfWeek = "";

                if (ticket.small_date) {
                    try {
                        const [year, month, day] = ticket.small_date.split("-");
                        const date = new Date(
                            parseInt(year),
                            parseInt(month) - 1,
                            parseInt(day)
                        );

                        const locale = resolveDateLocale(this.lang);

                        formattedDate = date.toLocaleDateString(locale, {
                            month: "short",
                            day: "numeric"
                        });

                        dayOfWeek = date.toLocaleDateString(locale, {
                            weekday: "long"
                        });
                    } catch (error) {
                        console.warn("Erro ao formatar data:", error);
                        formattedDate = ticket.small_date;
                        dayOfWeek = "";
                    }
                }

                let priceOld = null;
                let currentPrice = ticket.price;
                let discountCoefficient = 1;

                if (
                    this.config.allow_promocode &&
                    this.promotionalInfo &&
                    this.promotionalInfo.tickets &&
                    this.promotionalInfo.tickets.indexOf(ticket.id) !== -1 &&
                    !this.locationsWithoutPromoCode.includes(
                        ticket.location_slug
                    )
                ) {
                    priceOld = currentPrice;

                    if (this.promotionalInfo.promo_type === "fixed") {
                        // Fixed discount: subtract the discount amount
                        currentPrice = Math.floor(
                            currentPrice - this.promotionalInfo.discount
                        );
                    } else {
                        // Percent discount: multiply by the discount factor
                        discountCoefficient = this.promotionalInfo.discount;
                        currentPrice = Math.floor(
                            currentPrice * discountCoefficient
                        );
                    }
                }

                if (
                    !this.config.allow_promocode &&
                    ticket.price &&
                    ticket.price_ref
                ) {
                    priceOld = ticket.price_ref;
                    currentPrice = ticket.price;
                }

                const overlay = ticket.sku_id
                    ? this.offerOverlaysBySku[ticket.sku_id]
                    : null;
                const offerPricing = resolveOfferPricing(overlay);
                if (offerPricing) {
                    priceOld =
                        offerPricing.base != null
                            ? offerPricing.base
                            : priceOld || ticket.price_ref || ticket.price;
                    currentPrice = offerPricing.offer;
                }

                if (priceOld) {
                    priceOld = formatMoney(priceOld, this.config.currency);
                }

                if (currentPrice) {
                    currentPrice = formatMoney(
                        currentPrice,
                        this.config.currency
                    );
                }

                const catalogFlag = {
                    code: ticket.deal,
                    text: ticket.deal_text,
                    title: ticket.deal_title
                };
                const allowCatalogDeals = this.config.allow_deals !== false;

                return {
                    id: ticket.id,
                    sku_id: ticket.sku_id || null,
                    hash: ticket.hash,
                    status: ticket.status,
                    soldout: Number(ticket.soldout) || 0,
                    status_text: ticket.status_text,
                    type: ticket.location_slug,
                    title: ticket.description,
                    location: this.getLocationName(
                        ticket.location_slug,
                        ticket.location
                    ),
                    small_date: ticket.small_date,
                    sector: ticket.sector,
                    rows: ticket.rows,
                    date: formattedDate,
                    dayOfWeek: dayOfWeek,
                    group: ticket.group,
                    currency: currencySymbol(this.config.currency),
                    price_ref: ticket.price_ref,
                    price: currentPrice,
                    price_old: priceOld ? priceOld : null,
                    ticket_type: ticket.is_digital
                        ? "digital-ticket"
                        : "physical-ticket",
                    flag: resolveDisplayFlag(
                        overlay,
                        catalogFlag,
                        allowCatalogDeals
                    ),
                    offer_token: resolveOfferToken(overlay),
                    attribution_token: resolveAttributionToken(overlay),
                    experiment_group:
                        overlay && overlay.experiment_group
                            ? String(overlay.experiment_group)
                            : null,
                    offer_assignment_id: resolveOfferAssignmentId(overlay),
                    has_offer: Boolean(resolveOfferToken(overlay)),
                    offer_price_amount: offerPricing
                        ? offerPricing.offer
                        : null,
                    offer_base_amount:
                        offerPricing && offerPricing.base != null
                            ? offerPricing.base
                            : null,
                    offer_discount_percent:
                        offerPricing &&
                        Number.isFinite(Number(overlay.pricing.discount_percent))
                            ? Math.round(Number(overlay.pricing.discount_percent))
                            : null,
                    offer_copy: resolveOfferHighlights(overlay),
                    offer_urgency_label: resolveOfferUrgencyLabel(overlay),
                    img: ticket.img || null,
                    includes: ticket.additionals,
                    included_free: ticket.includes
                };
            });
        },
        filteredItems() {
            let filtered = [...this.items];

            if (this.filters.dates.length > 0) {
                filtered = filtered.filter(item => {
                    return this.filters.dates.some(date => {
                        if (!date) return true;

                        return item.small_date === date;
                    });
                });
            }

            if (this.filters.types.length > 0) {
                filtered = filtered.filter(item => {
                    return this.filters.types.some(type => {
                        if (!type) return true;
                        return item.type === type;
                    });
                });
            }

            if (this.filters.prices.length > 0) {
                filtered = filtered.filter(item => {
                    return this.filters.prices.some(price => {
                        if (!price) return true;

                        const priceRange = price.value.split("-");
                        if (priceRange.length === 2) {
                            const min = parseFloat(priceRange[0]);
                            const max = parseFloat(priceRange[1]);

                            return (
                                item.price_ref >= min && item.price_ref <= max
                            );
                        }
                        return true;
                    });
                });
            }

            if (this.filters.inclusions.length > 0) {
                filtered = filtered.filter(item => {
                    return this.filters.inclusions.some(inclusion => {
                        if (!inclusion) return true;
                        return item.includes.some(
                            include => include.name === inclusion
                        );
                    });
                });
            }

            if (this.filters.locationAndSector.length > 0) {
                filtered = filtered.filter(item => {
                    return this.filters.locationAndSector.some(
                        locationAndSector => {
                            if (!locationAndSector) return true;

                            const parts = locationAndSector.split("-");
                            const sector = parts.pop();
                            const location = parts.join("-");

                            return (
                                item.type === location &&
                                String(item.sector) === String(sector)
                            );
                        }
                    );
                });
            }

            this.highlightedItems = filtered;

            return this.applySorting(filtered);
        },
        paginatedItems() {
            const totalItems = this.filteredItems.length;
            const endIndex = this.currentPage * this.itemsPerPage;

            this.hasReachedEnd = endIndex >= totalItems;

            return this.filteredItems.slice(0, endIndex);
        },
        totalPages() {
            return Math.ceil(this.filteredItems.length / this.itemsPerPage);
        },
        filterOptions() {
            if (!this.itemsFiltered || this.itemsFiltered.length === 0) {
                return {
                    dates: [],
                    types: [],
                    prices: [],
                    inclusions: [],
                    locationAndSector: []
                };
            }

            const dates = [
                ...new Set(
                    this.itemsFiltered
                        .map(ticket => ticket.small_date)
                        .filter(Boolean)
                )
            ]
                .sort()
                .map(date => {
                    const ticketWithDate = this.itemsFiltered.find(
                        ticket => ticket.small_date === date
                    );
                    const group = ticketWithDate ? ticketWithDate.group : "";

                    try {
                        const [year, month, day] = date.split("-");
                        const dateObj = new Date(
                            parseInt(year),
                            parseInt(month) - 1,
                            parseInt(day)
                        );
                        const isBRL = this.config.currency === "BRL";

                        if (isBRL) {
                            return {
                                date: dateObj.toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit"
                                }),
                                group: group,
                                value: date
                            };
                        } else {
                            return {
                                date: dateObj.toLocaleDateString(
                                    resolveDateLocale(this.lang),
                                    {
                                        month: "short",
                                        day: "numeric"
                                    }
                                ),
                                group: group,
                                value: date
                            };
                        }
                    } catch (error) {
                        return {
                            date: date,
                            group: group,
                            value: date
                        };
                    }
                });

            const types = [
                ...new Set(
                    this.itemsFiltered
                        .map(ticket => ticket.location_slug)
                        .filter(Boolean)
                )
            ].map(type => {
                const ticket = this.itemsFiltered.find(
                    t => t.location_slug === type
                );
                const locationName = ticket
                    ? this.getLocationName(
                          ticket.location_slug,
                          ticket.location
                      )
                    : type;
                return {
                    label: locationName,
                    value: type
                };
            });

            const priceRanges = [];

            const allInclusions = this.itemsFiltered.flatMap(
                ticket => ticket.additionals || []
            );
            const inclusions = Array.from(
                new Map(
                    allInclusions.map(inclusion => [
                        JSON.stringify(inclusion),
                        inclusion
                    ])
                ).values()
            ).map(inclusion => ({
                label: inclusion.name,
                value: inclusion.name,
                icon: inclusion.icon
            }));

            return {
                dates,
                types,
                prices: priceRanges,
                inclusions
            };
        }
    },
    watch: {
        filteredItems: {
            handler() {
                this.resetPagination();
            },
            deep: true
        },
        isFilterDrawerOpen(value) {
            if (value) {
                document.body.classList.add("modal");
            } else {
                document.body.classList.remove("modal");
            }
        },
        "filters.types": {
            handler(value) {
                if (this.showItemsOnly) return;
                const { pathname, search } = window.location;
                const queryParams = search;
                const hasOnlyOneType = value.length === 1;

                const getTranslatedSlug = locationSlug => {
                    if (this.locations && this.locations.length > 0) {
                        const translatedLocation = this.locations.find(
                            loc => loc.slug_en === locationSlug
                        );
                        if (translatedLocation && translatedLocation.slug) {
                            return translatedLocation.slug;
                        }
                    }

                    const translatedSlugKey = Object.keys(locationsSlugs).find(
                        key => locationsSlugs[key] === locationSlug
                    );
                    if (translatedSlugKey) {
                        return translatedSlugKey;
                    }

                    return locationSlug;
                };

                const translatedSlug = value[0]
                    ? getTranslatedSlug(value[0])
                    : "";
                const hasSlugInPathname = !!(
                    translatedSlug && pathname.includes(translatedSlug)
                );

                if (hasOnlyOneType) {
                    if (hasSlugInPathname) return;

                    history.replaceState(
                        {},
                        null,
                        this.removeEndSlash(pathname) +
                            "/" +
                            translatedSlug +
                            queryParams
                    );

                    return;
                }

                const removeSlugFromPath = slugToRemove => {
                    if (!slugToRemove) return false;
                    const slugAtEndRegex = new RegExp(`/${slugToRemove}/?$`);

                    if (slugAtEndRegex.test(pathname)) {
                        const pathnameWithoutType = pathname.replace(
                            slugAtEndRegex,
                            ""
                        );

                        let pathnameWithoutSlash = this.removeEndSlash(
                            pathnameWithoutType
                        );

                        if (pathnameWithoutSlash === "") {
                            pathnameWithoutSlash = "/";
                        }

                        history.replaceState(
                            {},
                            null,
                            pathnameWithoutSlash + queryParams
                        );

                        return true;
                    }

                    return false;
                };

                const allLocationSlugs = [
                    ...new Set(
                        this.itemsFiltered
                            .map(ticket => ticket.location_slug)
                            .filter(Boolean)
                    )
                ];

                allLocationSlugs.forEach(locationSlug => {
                    const translatedSlugForLocation = getTranslatedSlug(
                        locationSlug
                    );

                    removeSlugFromPath(translatedSlugForLocation);
                });
            }
        }
    },
    methods: {
        trackQuizEvent(eventName) {
            pushDataLayer({
                event: eventName,
                event_id: createEventId("quiz"),
                quiz_name: "ticket_quiz"
            });
        },
        handleQuizLauncherShown() {
            this.trackQuizEvent("quiz_launcher_shown");
        },
        handleQuizOpen() {
            this.quizOpen = true;
            this.trackQuizEvent("quiz_opened");
        },
        openQuizFromUrl() {
            const url = new URL(window.location.href);
            if (!url.searchParams.has("bquiz")) return;

            url.searchParams.delete("bquiz");
            window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);

            if (!this.enableTicketQuiz) return;
            this.quizOpen = true;
            pushDataLayer({
                event: "quiz_opened",
                event_id: createEventId("quiz"),
                quiz_name: "ticket_quiz",
                quiz_entry: "url"
            });
        },
        handleQuizMinimized() {
            this.trackQuizEvent("quiz_minimized");
        },
        handleFilterOpen(value) {
            this.isFilterDrawerOpen = value;
        },
        handleFilterChange(filters) {
            this.recordFilterInterest(this.filters, filters);
            this.filters = { ...filters };
            this.resetPagination();
        },
        recordFilterInterest(previous, next) {
            const added = key =>
                (next[key] || []).find(
                    value => value && !(previous[key] || []).includes(value)
                );
            const type = added("types");
            const day = added("dates");
            if (!type && !day) return;

            const payload = { path: window.location.pathname };
            if (type) payload.type = String(type);
            if (day) payload.day = String(day).slice(0, 10);
            window.axios
                .post("/api/ticket-navigation", payload, { timeout: 3000 })
                .catch(() => {});
        },
        handleMapPreview(preview) {
            this.mapHoverPreview = preview;
        },
        handlePromocodeChange(promotionalInfo) {
            this.promotionalInfo = promotionalInfo;
        },
        handleSortChange(sortBy) {
            this.sortBy = sortBy;
            trackSelectContent({
                content_type: "sort",
                content_id: sortBy
            });
            this.resetPagination();
        },
        resetPagination() {
            this.currentPage = 1;
            this.hasReachedEnd = false;
            this.isLoadingMore = false;
            this.$nextTick(() => this.setupLoadMoreObserver());
        },
        applySorting(items) {
            const sorted = [...items];
            // Dynamic offer treatment cards always lead the list; secondary
            // sort keeps the user's chosen order among offer / non-offer groups.
            const withOfferFirst = compare => (a, b) => {
                const offerRank =
                    Number(Boolean(b && b.has_offer)) -
                    Number(Boolean(a && a.has_offer));
                if (offerRank !== 0) {
                    return offerRank;
                }
                return compare(a, b);
            };

            switch (this.sortBy) {
                case "price-low-high":
                    return sorted.sort(
                        withOfferFirst((a, b) => {
                            const priceA = this.extractPrice(a.price);
                            const priceB = this.extractPrice(b.price);
                            return priceA - priceB;
                        })
                    );

                case "price-high-low":
                    return sorted.sort(
                        withOfferFirst((a, b) => {
                            const priceA = this.extractPrice(a.price);
                            const priceB = this.extractPrice(b.price);
                            return priceB - priceA;
                        })
                    );

                case "date":
                    return sorted.sort(
                        withOfferFirst((a, b) => {
                            const originalA = this.itemsFiltered.find(
                                ticket =>
                                    ticket.location_slug === a.type &&
                                    ticket.description === a.title
                            );
                            const originalB = this.itemsFiltered.find(
                                ticket =>
                                    ticket.location_slug === b.type &&
                                    ticket.description === b.title
                            );

                            if (!originalA || !originalB) return 0;

                            const dateA = new Date(originalA.small_date);
                            const dateB = new Date(originalB.small_date);
                            return dateA - dateB;
                        })
                    );

                case "most-relevant":
                default:
                    return sorted.sort(withOfferFirst(() => 0));
            }
        },
        extractPrice(priceString) {
            const match = priceString.match(/[\d,]+\.?\d*/);
            if (match) {
                return parseFloat(match[0].replace(",", ""));
            }
            return 0;
        },
        setupLoadMoreObserver() {
            this.teardownLoadMoreObserver();

            if (this.hasReachedEnd) {
                return;
            }

            if (!this.onWindowScrollLoadMore) {
                let ticking = false;
                this.onWindowScrollLoadMore = () => {
                    if (ticking) return;
                    ticking = true;
                    let ran = false;
                    const run = () => {
                        if (ran) return;
                        ran = true;
                        ticking = false;
                        this.ensureMoreItemsWhileVisible();
                    };
                    if (typeof window.requestAnimationFrame === "function") {
                        window.requestAnimationFrame(run);
                    }
                    // Fallback if rAF is starved (background tab / some automations).
                    window.setTimeout(run, 50);
                };
                window.addEventListener("scroll", this.onWindowScrollLoadMore, {
                    passive: true
                });
                window.addEventListener("resize", this.onWindowScrollLoadMore, {
                    passive: true
                });
            }

            const sentinel = this.$refs.loadMoreSentinel;
            if (
                sentinel &&
                typeof IntersectionObserver !== "undefined"
            ) {
                this.loadMoreObserver = new IntersectionObserver(
                    entries => {
                        const entry = entries[0];
                        if (!entry || !entry.isIntersecting) {
                            return;
                        }
                        this.loadMoreItems({ fromScroll: true });
                    },
                    {
                        root: null,
                        rootMargin: "240px 0px",
                        threshold: 0
                    }
                );
                this.loadMoreObserver.observe(sentinel);
            }

            this.$nextTick(() => this.ensureMoreItemsWhileVisible());
        },
        teardownLoadMoreObserver() {
            if (this.loadMoreObserver) {
                this.loadMoreObserver.disconnect();
                this.loadMoreObserver = null;
            }
        },
        teardownLoadMoreScrollListener() {
            if (!this.onWindowScrollLoadMore) {
                return;
            }
            window.removeEventListener("scroll", this.onWindowScrollLoadMore);
            window.removeEventListener("resize", this.onWindowScrollLoadMore);
            this.onWindowScrollLoadMore = null;
        },
        ensureMoreItemsWhileVisible() {
            const sentinel = this.$refs.loadMoreSentinel;
            if (!sentinel || this.hasReachedEnd || this.isLoadingMore) {
                return;
            }

            const rect = sentinel.getBoundingClientRect();
            const viewportBottom = window.innerHeight + 240;
            if (rect.top <= viewportBottom) {
                this.loadMoreItems({ fromScroll: true });
            }
        },
        async loadMoreItems(options = {}) {
            if (this.isLoadingMore || this.hasReachedEnd) {
                return;
            }

            this.isLoadingMore = true;

            // Keep a short delay so the loading label is perceptible.
            await new Promise(resolve => setTimeout(resolve, 180));

            this.currentPage++;
            trackSelectContent({
                content_type: options.fromScroll
                    ? "load_more_scroll"
                    : "load_more",
                content_id: String(this.currentPage)
            });
            this.isLoadingMore = false;

            await this.$nextTick();
            if (!this.hasReachedEnd) {
                this.setupLoadMoreObserver();
                this.ensureMoreItemsWhileVisible();
            }
        },
        removeEndSlash(value) {
            return value.endsWith("/") ? value.slice(0, -1) : value;
        },
        getLocationName(locationSlug, fallbackName) {
            if (!this.locations || this.locations.length === 0) {
                return fallbackName || locationSlug;
            }

            const translatedLocation = this.locations.find(
                loc => loc.slug_en === locationSlug
            );

            if (translatedLocation && translatedLocation.name) {
                return translatedLocation.name;
            }

            const translatedLocationBySlug = this.locations.find(
                loc => loc.slug === locationSlug
            );

            if (translatedLocationBySlug && translatedLocationBySlug.name) {
                return translatedLocationBySlug.name;
            }

            return fallbackName || locationSlug;
        },
        trackListView() {
            const list = this.filteredItems || [];
            if (!list.length) return;
            trackViewItemList({
                item_list_id: "ticket_packages",
                item_list_name: this.title || "Ticket Packages",
                currency: resolveCurrency(this.config.currency),
                items: list
                    .slice(0, 30)
                    .map(t => ticketToItem(t))
                    .filter(Boolean)
            });
        },
        async loadOfferOverlays() {
            const skuIds = [
                ...new Set(
                    (this.itemsFiltered || [])
                        .map(ticket => ticket.sku_id)
                        .filter(Boolean)
                )
            ];
            if (!skuIds.length) {
                return;
            }

            const bySku = await fetchOfferOverlays({
                skuIds,
                lang: this.lang,
                channel: "SHOP"
            });
            this.offerOverlaysBySku = bySku || {};
            this.refreshOfferAnchorState();
        },
        setupOfferAnchorWatchers() {
            if (this.onWindowScrollOfferAnchor) {
                return;
            }

            let ticking = false;
            this.onWindowScrollOfferAnchor = () => {
                if (ticking) return;
                ticking = true;
                let ran = false;
                const run = () => {
                    if (ran) return;
                    ran = true;
                    ticking = false;
                    this.syncOfferCardVisibilityFromDom();
                    this.measureOfferAnchorPlacement();
                };
                if (typeof window.requestAnimationFrame === "function") {
                    window.requestAnimationFrame(run);
                }
                window.setTimeout(run, 50);
            };

            window.addEventListener("scroll", this.onWindowScrollOfferAnchor, {
                passive: true
            });
            window.addEventListener("resize", this.onWindowScrollOfferAnchor, {
                passive: true
            });
        },
        teardownOfferAnchorWatchers() {
            if (!this.onWindowScrollOfferAnchor) {
                return;
            }
            window.removeEventListener(
                "scroll",
                this.onWindowScrollOfferAnchor
            );
            window.removeEventListener(
                "resize",
                this.onWindowScrollOfferAnchor
            );
            this.onWindowScrollOfferAnchor = null;
        },
        measureOfferAnchorPlacement() {
            const isMobile =
                typeof window.matchMedia === "function" &&
                window.matchMedia("(max-width: 799px)").matches;

            // Compact dock bar is mobile-only (bottom chrome; hides header-mobile).
            if (isMobile) {
                this.offerAnchorPlacement = "dock";
                this.$nextTick(() => {
                    this.syncOfferDockBodyClass(this.showOfferDockChrome);
                });
                return;
            }

            const desktopMapVisible =
                typeof window.matchMedia === "function" &&
                window.matchMedia("(min-width: 992px)").matches;

            if (!desktopMapVisible || !this.$refs.ticketMapColumn) {
                this.offerAnchorPlacement = "none";
                return;
            }

            // Desktop: map column only — never fall back to the bottom dock.
            const mapBlock = this.$el.querySelector(
                ".ticket-packages-filter-map"
            );
            if (!mapBlock) {
                this.offerAnchorPlacement = "none";
                return;
            }

            const bottom = mapBlock.getBoundingClientRect().bottom;
            const spaceBelow = window.innerHeight - bottom - 16;
            this.offerAnchorPlacement =
                spaceBelow >= OFFER_ANCHOR_MIN_SPACE_PX ? "map" : "none";
        },
        syncOfferCardVisibilityFromDom() {
            // isOfferCardVisible === true → keep dock hidden.
            // Dock only after the list offer card has scrolled fully above the sticky filter
            // (not while it is still in view, and not while it is still below the fold).
            if (!this.primaryOffer || !this.$el) {
                this.isOfferCardVisible = false;
                return false;
            }

            const nodes = this.$el.querySelectorAll("[data-offer-card]");
            if (!nodes.length) {
                // Offer exists in data but list card not painted yet — keep sticky hidden.
                this.isOfferCardVisible = true;
                return true;
            }

            let measured = false;
            let stillAheadOrInView = false;
            nodes.forEach(node => {
                const rect = node.getBoundingClientRect();
                if (rect.width <= 0 || rect.height <= 0) return;
                measured = true;
                // Any pixel still at/below the filter edge → card not scrolled past yet.
                if (rect.bottom > OFFER_FILTER_STICKY_HEIGHT_PX) {
                    stillAheadOrInView = true;
                }
            });

            if (!measured) {
                this.isOfferCardVisible = true;
                return true;
            }

            this.isOfferCardVisible = stillAheadOrInView;
            return stillAheadOrInView;
        },
        setupOfferVisibilityObserver() {
            this.teardownOfferVisibilityObserver();

            if (!this.primaryOffer) {
                this.isOfferCardVisible = false;
                this.measureOfferAnchorPlacement();
                return;
            }

            const nodes = this.$el
                ? this.$el.querySelectorAll("[data-offer-card]")
                : [];
            if (!nodes.length) {
                // Not painted yet — never show sticky until the list card has been seen leaving.
                this.isOfferCardVisible = true;
                this.measureOfferAnchorPlacement();
                return;
            }

            if (typeof IntersectionObserver !== "undefined") {
                this.offerVisibilityObserver = new IntersectionObserver(
                    () => {
                        this.syncOfferCardVisibilityFromDom();
                        this.measureOfferAnchorPlacement();
                    },
                    {
                        root: null,
                        // Ignore the sticky filter band so "leaving" matches dock reveal.
                        rootMargin: `-${OFFER_FILTER_STICKY_HEIGHT_PX}px 0px 0px 0px`,
                        threshold: [0, OFFER_CARD_VISIBILITY_RATIO, 0.25, 0.5, 1]
                    }
                );

                nodes.forEach(node => {
                    this.offerVisibilityObserver.observe(node);
                });
            }

            this.syncOfferCardVisibilityFromDom();
            this.measureOfferAnchorPlacement();
        },
        teardownOfferVisibilityObserver() {
            if (this.offerVisibilityObserver) {
                this.offerVisibilityObserver.disconnect();
                this.offerVisibilityObserver = null;
            }
        },
        refreshOfferAnchorState() {
            this.$nextTick(() => {
                this.setupOfferVisibilityObserver();
                this.syncOfferCardVisibilityFromDom();
                this.measureOfferAnchorPlacement();
                this.syncOfferDockBodyClass(this.showOfferDockChrome);
            });
        },
        async handleOfferAnchorCta() {
            const offer = this.primaryOffer;
            if (!offer) return;

            trackSelectContent({
                content_type: "offer_anchor_cta",
                content_id: String(
                    offer.sku_id || offer.id || offer.hash || "offer"
                )
            });

            await this.navigateToOfferDetails(offer);
        },
        async navigateToOfferDetails(offer) {
            if (
                offer &&
                offer.offer_token &&
                Number.isFinite(Number(offer.offer_price_amount))
            ) {
                saveOfferHandoff({
                    sku_id: offer.sku_id,
                    hash: offer.hash,
                    token: offer.offer_token,
                    assignment_id: offer.offer_assignment_id || null,
                    experiment_group: "treatment",
                    offer_price_amount: Number(offer.offer_price_amount),
                    base_price_amount:
                        offer.offer_base_amount != null
                            ? Number(offer.offer_base_amount)
                            : null,
                    discount_percent:
                        offer.offer_discount_percent != null
                            ? Number(offer.offer_discount_percent)
                            : null
                });
            }

            if (offer && offer.offer_assignment_id) {
                await recordOfferClick({
                    assignmentId: offer.offer_assignment_id,
                    source: "portal_sticky_cta"
                });
            }

            const baseUrl = window.location.origin;
            const route = (this.page && this.page.route) || "";
            const hash = offer.hash || "";
            let baseRoute = route;
            if (baseRoute.startsWith("/")) {
                baseRoute = baseRoute.substring(1);
            }

            const pageUrl = new URL(`${baseUrl}/${baseRoute}/${hash}`);
            const currentUrlParams = new URLSearchParams(
                window.location.search
            );
            currentUrlParams.forEach((value, key) => {
                pageUrl.searchParams.set(key, value);
            });

            if (offer && offer.offer_token) {
                pageUrl.searchParams.set(
                    "offer_token",
                    String(offer.offer_token)
                );
                if (offer.sku_id) {
                    pageUrl.searchParams.set("sku_id", String(offer.sku_id));
                }
            }

            window.location.href = pageUrl.toString();
        },
        syncOfferDockBodyClass(active) {
            if (typeof document === "undefined" || !document.body) {
                return;
            }
            const on = Boolean(active);
            document.body.classList.toggle("offer-dock-active", on);
            // Belt-and-suspenders: some caches miss the body CSS toggle
            const header = document.querySelector("header.header-mobile");
            if (header) {
                header.setAttribute("aria-hidden", on ? "true" : "false");
                header.hidden = on;
            }
        }
    },
    watch: {
        paginatedItems() {
            this.refreshOfferAnchorState();
        },
        primaryOffer() {
            this.refreshOfferAnchorState();
        },
        showOfferDockChrome: {
            immediate: true,
            handler(active) {
                this.syncOfferDockBodyClass(active);
            }
        }
    },
    mounted() {
        this.openQuizFromUrl();

        const locationsSlugs = this.trans.tickets.locations.slugs;
        const pathnameWithoutTrailingSlash = this.removeEndSlash(
            window.location.pathname
        );

        const ticketType = pathnameWithoutTrailingSlash.split("/").pop();
        const ticketTypeKey = Object.keys(locationsSlugs).find(
            key => locationsSlugs[key] === ticketType
        );

        if (ticketTypeKey) {
            this.ticketType = ticketTypeKey;
            this.filters.types.push(ticketTypeKey);
        }

        this.loadOfferOverlays();
        this.setupOfferAnchorWatchers();
        this.$nextTick(() => {
            this.trackListView();
            this.setupLoadMoreObserver();
            this.refreshOfferAnchorState();
        });
    },
    beforeDestroy() {
        this.syncOfferDockBodyClass(false);
        this.teardownLoadMoreObserver();
        this.teardownLoadMoreScrollListener();
        this.teardownOfferVisibilityObserver();
        this.teardownOfferAnchorWatchers();
    }
};
</script>
